import {
  formatCompactCurrency,
  formatCount,
  formatMonthShort,
  formatPercent,
  formatRatio,
  formatShortDate,
} from '../../../lib/format.js';
import { CREATIVE_SCORE_METRIC_NAME, TOTAL_TAGS_METRIC_NAME } from './displayedMetrics.js';

// Matches the `precision: 2` the legacy product stores on its percentage metrics, so a ROAS of
// 0.0025238 reads as 0.25% here exactly as it does in the old app rather than rounding to 0.3%.
const PERCENT_DIGITS = 2;

const BUCKET_ADJECTIVE = { day: 'daily', week: 'weekly', month: 'monthly' } as const;

const BUCKET_NOUN = { day: 'Day', week: 'Week', month: 'Month' } as const;

export type MetricFormat = 'compact_currency' | 'percentage' | 'number' | 'ratio';

export type TrendGranularity = 'day' | 'week' | 'month';

export function formatMetricValue(
  value: number | null,
  format: MetricFormat,
  currencySymbol: string,
): string {
  if (format === 'compact_currency') return formatCompactCurrency(value, currencySymbol);
  if (format === 'percentage') return formatPercent(value, PERCENT_DIGITS);
  if (format === 'ratio') return formatRatio(value);
  return formatCount(value);
}

export function formatTrendValue(
  name: string,
  value: number | null,
  format: MetricFormat,
  currencySymbol: string,
): string {
  if (name === CREATIVE_SCORE_METRIC_NAME) return value === null ? '-' : value.toFixed(1);
  if (name === TOTAL_TAGS_METRIC_NAME) return formatCount(value, 2);
  return formatMetricValue(value, format, currencySymbol);
}

export function bucketLabel(date: string, granularity: TrendGranularity): string {
  return granularity === 'month' ? formatMonthShort(date) : formatShortDate(date);
}

export function bucketCountLabel(count: number, granularity: TrendGranularity): string {
  return `${count} ${BUCKET_ADJECTIVE[granularity]} bucket${count === 1 ? '' : 's'}`;
}

export function bucketTickLabel(bucket: number, granularity: TrendGranularity): string {
  return `${BUCKET_NOUN[granularity]} ${bucket}`;
}

export function formatDelta(delta: number | null): string {
  if (delta === null) return 'n/a';
  const percent = delta * 100;
  return `${percent > 0 ? '+' : ''}${percent.toFixed(1)}%`;
}

export function rangeLabel(dateRange: readonly [string, string]): string {
  return `${formatShortDate(dateRange[0])} – ${formatShortDate(dateRange[1])}`;
}

export function rangeDayCount(dateRange: readonly [string, string]): number | null {
  const span = Date.parse(`${dateRange[1]}T00:00:00Z`) - Date.parse(`${dateRange[0]}T00:00:00Z`);
  if (Number.isNaN(span)) return null;
  return Math.round(span / 86_400_000) + 1;
}

export function trendHeadline(
  label: string,
  delta: number | null,
  direction: 'up' | 'down' | 'flat',
): string {
  if (delta === null) return `${label} has no comparable previous period`;
  if (direction === 'flat') return `${label} is unchanged`;
  const magnitude = `${Math.abs(delta * 100).toFixed(1)}%`;
  return `${label} ${direction === 'up' ? 'increased' : 'decreased'} by ${magnitude}`;
}
